// 1NCE SIM evidence on Assign Radios, Device Details and Deployment (documentation/onence.md).
// Platform administrators only. Assign Radios and Deployment check on demand (they cost 1NCE API
// requests); Device Details loads its SIM summary once when shown. The server chooses which ICCID is
// looked up from its own evidence and which Management API version (one global setting). Only Device
// Details offers SIM actions (OnenceSimActions.tsx, server switch off by default); nothing here starts
// a remote installation (that is RemoteInstall.tsx, gated by the server).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  checkOnenceSim, displayIccid, displayImei, formatMB, ONENCE_PORTAL_URL, onenceDate, onenceErrorText, type OnenceAssessment, type OnenceCandidate,
  type OnenceQuota, type OnenceSubjectKind,
} from '../../services/onence';
import { StatusBadge } from './configuration/ConfigurationViews';
import { OnenceSimActions } from './OnenceSimActions';

const sourceText: Record<string, string> = {
  USB_ADB: 'USB observation (read over ADB, record matched to this radio)',
  USB_ADB_UNMATCHED: 'USB observation (record not yet matched to an enrolled radio)',
  SELF_REPORTED: 'reported by the radio itself (lower trust)',
};

const checkText: Record<string, string> = {
  iccid_sources: 'ICCID sources',
  provider_iccid: '1NCE SIM',
  imei: 'IMEI',
  ip: 'IP address',
  connectivity: 'Data session',
};

const resultMark: Record<string, { mark: string; cls: string }> = {
  match: { mark: '✓', cls: 'text-green-300' },
  mismatch: { mark: '✗', cls: 'text-red-300' },
  not_seen: { mark: '!', cls: 'text-yellow-200' },
  unavailable: { mark: '–', cls: 'text-gray-400' },
};

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '');

const connectivityText: Record<string, string> = {
  ONLINE: 'Online (active data session)',
  ATTACHED: 'Attached (registered, no data session; may be stale)',
  OFFLINE: 'Offline',
  BLOCKED: 'Blocked',
  UNKNOWN: 'Unknown',
  UNRECOGNISED: 'Unrecognised answer',
};

const connectivityTone: Record<string, string> = {
  ONLINE: 'bg-green-900/40 border-green-700 text-green-200',
  ATTACHED: 'bg-yellow-900/40 border-yellow-700 text-yellow-200',
  OFFLINE: 'bg-gray-700 border-gray-600 text-gray-200',
  BLOCKED: 'bg-red-900/40 border-red-700 text-red-200',
};

function Pill({ text, tone, data }: { text: string; tone?: string; data?: string }) {
  return <span data-onence-pill={data} className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${tone ?? 'bg-gray-700 border-gray-600 text-gray-200'}`}>{text}</span>;
}

function Item({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  const empty = children === undefined || children === null || children === '';
  return (
    <div>
      <dt className="text-xs text-gray-400 uppercase font-semibold">{label}</dt>
      <dd className={`text-sm ${empty ? 'text-gray-500' : 'text-white'} ${mono && !empty ? 'font-mono' : ''}`}>{empty ? 'not reported' : children}</dd>
    </div>
  );
}

/** The data quota: used / total / remaining with a progress bar only when 1NCE's numbers are
 *  consistent. Its expiry is shown in the summary as "Udløbsdato (datakvote)": the expiry of THIS data
 *  quota, never a SIM or contract end. */
export function OnenceQuotaView({ quota, error }: { quota?: OnenceQuota; error?: string }) {
  if (!quota) {
    return (
      <p className="text-xs text-gray-400" data-onence-quota="unavailable">
        Data quota unavailable{error ? ` (${onenceErrorText[error] ?? error})` : ''}. The SIM details above are not affected.
      </p>
    );
  }
  const pct = quota.used_percent;
  const bar = pct === undefined ? null : Math.min(100, Math.max(0, pct));
  return (
    <div className="space-y-1" data-onence-quota="available">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="text-white">
          {quota.used_mb !== undefined && quota.total_mb !== undefined
            ? `${formatMB(quota.used_mb)} used of ${formatMB(quota.total_mb)}`
            : quota.total_mb !== undefined ? `Total ${formatMB(quota.total_mb)}` : 'Usage not reported'}
        </span>
        {quota.remaining_mb !== undefined && <span className="text-gray-300">{`${formatMB(quota.remaining_mb)} remaining`}</span>}
      </div>
      {bar !== null && (
        <div className="h-2 w-full rounded bg-gray-700 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={bar}
          aria-label="Data quota used" data-onence-quota-bar={bar}>
          <div className={`h-2 ${bar >= 90 ? 'bg-red-500' : bar >= 80 ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{ width: `${bar}%` }} />
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 text-xs text-gray-400">
        {pct !== undefined && <span>{`${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })} % used`}</span>}
        {quota.state && <span data-onence-quota-state={quota.state}>Quota {quota.state.toLowerCase()}</span>}
      </div>
    </div>
  );
}

/** The rich current SIM summary (Device Details). Partial answers stay visible: a failed status or
 *  quota request never hides the SIM details. The SIM actions are a separate panel (OnenceDeviceSim). */
export function OnenceSimSummary({ a }: { a: OnenceAssessment }) {
  const sim = a.sim;
  const c = a.connectivity;
  const version = a.api_version;
  // The data session's own IP is shown only when it differs from the SIM IP (otherwise a duplicate).
  const sessionIPs = c?.session_ips ?? (c?.session_ip ? [c.session_ip] : []);
  const otherSessionIPs = sim?.ip_address ? sessionIPs.filter((ip) => ip !== sim.ip_address) : [];
  const actionsOn = !!a.actions?.enabled;
  return (
    <div className="space-y-3 rounded border border-gray-700 bg-gray-900/40 p-3" data-onence-summary={sim ? 'sim' : 'no-sim'} data-onence-version={version || ''}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-white font-semibold">{sim?.label || '1NCE SIM'}</span>
        {sim?.status && <Pill data="sim-status" text={`SIM ${sim.status}`} tone={sim.status === 'Enabled' ? 'bg-green-900/40 border-green-700 text-green-200' : undefined} />}
        {c && <Pill data="connectivity" text={c.status} tone={connectivityTone[c.status]} />}
      </div>
      {a.iccid && <IccidWithPortal iccid={a.iccid} />}
      {!a.configured && <p className="text-xs text-yellow-200">1NCE is not configured (Settings → Configuration → 1NCE SIM).</p>}
      {a.configured && !a.iccid && <p className="text-xs text-gray-400">RadioOS knows no ICCID for this radio, so 1NCE was not asked.</p>}
      {a.lookup_error && (
        <p className="text-xs text-red-300" data-onence-error={a.lookup_error}>
          1NCE SIM details unavailable: {onenceErrorText[a.lookup_error] ?? a.lookup_error}{a.retry_after_seconds ? ` (retry in ${a.retry_after_seconds} s)` : ''}
          {a.lookup_error === 'forbidden' && version ? ` with Management API ${version}. A legacy SIM may only be readable with the other API version (Settings → Configuration → 1NCE SIM).` : ''}
        </p>
      )}
      {sim && (
        <>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2" data-onence-summary-fields>
            <Item label="Connectivity">
              {c ? connectivityText[c.status] ?? c.status : a.status_error ? `unavailable (${onenceErrorText[a.status_error] ?? a.status_error})` : ''}
            </Item>
            <Item label="Network operator">{c?.operator}</Item>
            <Item label="Country">{c?.country ? `${c.country}${c.country_iso ? ` (${c.country_iso})` : ''}` : ''}</Item>
            <Item label="Radio access">{c?.rat}</Item>
            <Item label="IMEI (last seen by 1NCE)" mono>{sim.imei ? (
              <span data-onence-imei title={sim.imei !== displayImei(sim.imei) ? `IMEISV as reported by 1NCE: ${sim.imei}` : undefined}>
                {displayImei(sim.imei)}{sim.imei_lock ? ' · IMEI lock on' : ''}
              </span>
            ) : ''}</Item>
            <Item label="SIM IP" mono>{sim.ip_address}</Item>
            <Item label="Activation date">{onenceDate(sim.activation_date, true)}</Item>
            <Item label="Udløbsdato (datakvote)">{a.quota?.expires_at ? (
              <span data-onence-quota-expiry title="Expiry of the current data quota (1NCE quota/data expiry_date); not the SIM's own expiry">
                {onenceDate(a.quota.expires_at, true)}
              </span>
            ) : ''}</Item>
            <Item label="Last network update">{onenceDate(c?.last_updated_at)}</Item>
            {c?.data_limit && <Item label="Data traffic limit">{c.data_limit === 'REACHED' ? 'Reached' : 'Not reached'}</Item>}
          </dl>
          {otherSessionIPs.length > 0 && (
            <p className="text-xs text-yellow-200" data-onence-session-ip-differs>
              1NCE reports a data-session IP ({otherSessionIPs.join(', ')}) that differs from the SIM IP.
            </p>
          )}
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Data quota</p>
            <OnenceQuotaView quota={a.quota} error={a.quota_error} />
          </div>
        </>
      )}
      <p className="text-xs text-gray-500" data-onence-source>
        Source: 1NCE Management API {version || '(version unavailable)'}{a.checked_at ? ` · last checked ${when(a.checked_at)}` : ''}{actionsOn ? '' : ' · read-only'}
        {version === 'v1' ? ' · v1 is temporary compatibility for legacy SIMs (1NCE retires it at the end of 2026)' : ''}
      </p>
    </div>
  );
}

/** An ICCID with copy and a search aid for the 1NCE portal. It shows and copies the form 1NCE uses
 *  (a validated 20-digit ICCID without its Luhn check digit, displayIccid); the full ICCID stays in
 *  the data, the requests and the audit. 1NCE publishes no per-SIM link, so the link opens the
 *  documented portal root; the administrator searches for the copied ICCID. */
export function IccidWithPortal({ iccid, note }: { iccid: string; note?: string }) {
  const [copied, setCopied] = useState(false);
  const shown = displayIccid(iccid);
  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-onence-iccid>
      <span className="font-mono" title={shown !== iccid ? `ICCID with check digit: ${iccid}` : undefined}>{shown}</span>
      <button type="button" className="text-xs px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-100" aria-label="Copy ICCID"
        onClick={() => { navigator.clipboard?.writeText(shown).then(() => setCopied(true), () => setCopied(false)); }}>
        {copied ? 'Copied' : 'Copy'}
      </button>
      <a href={ONENCE_PORTAL_URL} target="_blank" rel="noopener noreferrer" data-onence-portal
        title="Opens the 1NCE portal; search for this ICCID under SIMs (1NCE publishes no direct SIM link)"
        className="text-xs text-blue-300 hover:underline">1NCE portal ↗ (search ICCID)</a>
      {note && <span className="text-xs text-gray-400">{note}</span>}
    </span>
  );
}

function CandidateRow({ c, selected }: { c: OnenceCandidate; selected: boolean }) {
  return (
    <li className="text-xs" data-onence-candidate={c.trust}>
      <span className="font-mono text-gray-100">{c.iccid}</span>
      <span className={`ml-2 ${c.trust === 'observed' ? 'text-gray-300' : 'text-yellow-200'}`}>{sourceText[c.source] ?? c.source}</span>
      {c.observed_at && <span className="ml-2 text-gray-500">{when(c.observed_at)}</span>}
      {selected && <span className="ml-2 text-blue-300">(looked up)</span>}
    </li>
  );
}

/** Pure presentation of one SIM check. */
export function OnenceAssessmentView({ a }: { a: OnenceAssessment }) {
  const r = a.readiness;
  return (
    <div className="space-y-2 text-sm" data-onence-assessment={r.state}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge state={r.state} />
        {a.checked_at && <span className="text-xs text-gray-500">checked {when(a.checked_at)}</span>}
      </div>
      {a.subject.candidates.length === 0 ? (
        <p className="text-xs text-gray-400">RadioOS knows no ICCID for this radio (no USB observation, no report from the radio).</p>
      ) : (
        <ul className="space-y-0.5">
          {a.subject.candidates.map((c) => <CandidateRow key={c.source + c.iccid} c={c} selected={c.iccid === a.iccid && c.source === a.iccid_source} />)}
        </ul>
      )}
      {a.iccid && <IccidWithPortal iccid={a.iccid} />}
      {!a.configured && <p className="text-xs text-yellow-200">1NCE is not configured (Settings → Configuration → 1NCE SIM); only RadioOS evidence is shown.</p>}
      {a.lookup_error && (
        <p className="text-xs text-red-300" data-onence-error={a.lookup_error}>
          1NCE lookup: {onenceErrorText[a.lookup_error] ?? a.lookup_error}{a.retry_after_seconds ? ` (retry in ${a.retry_after_seconds} s)` : ''}
        </p>
      )}
      {a.api_version && <p className="text-xs text-gray-500" data-onence-source>1NCE Management API {a.api_version}</p>}
      {a.sim && (
        <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-0.5 text-xs" data-onence-sim>
          <dt className="text-gray-400">1NCE SIM status</dt><dd className="text-gray-100">{a.sim.status || '—'}</dd>
          <dt className="text-gray-400">SIM IP (1NCE)</dt><dd className="font-mono text-gray-100">{a.sim.ip_address || '—'}</dd>
          <dt className="text-gray-400">Last IMEI (1NCE)</dt><dd className="font-mono text-gray-100">{displayImei(a.sim.imei) || '—'}{a.sim.imei_lock ? ' · IMEI lock on' : ''}</dd>
          <dt className="text-gray-400">Data session</dt>
          <dd className="text-gray-100">
            {a.connectivity ? a.connectivity.status : a.status_error ? `unavailable (${onenceErrorText[a.status_error] ?? a.status_error})` : '—'}
            {a.connectivity?.status === 'ATTACHED' && <span className="text-gray-400"> (registered without a data session; may be stale)</span>}
            {a.connectivity?.session_ip ? <span className="font-mono"> · {a.connectivity.session_ip}</span> : null}
          </dd>
        </dl>
      )}
      {a.checks.length > 0 && (
        <ul className="space-y-0.5 text-xs" data-onence-checks>
          {a.checks.map((c) => {
            const m = resultMark[c.result] ?? resultMark.unavailable;
            return <li key={c.name} data-onence-check={c.name} data-result={c.result}><span className={m.cls}>{m.mark}</span> <span className="text-gray-300">{checkText[c.name] ?? c.name}:</span> <span className="text-gray-400">{c.detail}</span></li>;
          })}
        </ul>
      )}
      {r.reasons.length > 0 && <p className="text-xs text-gray-400">Readiness: {r.reasons.join('; ')}</p>}
      <p className="text-xs text-gray-500">
        The SIM IP is the address 1NCE assigns to the SIM; it does not by itself prove which radio holds the SIM. 1NCE data is enrichment, never identity.
      </p>
    </div>
  );
}

/** On-demand SIM check for one subject (button, then the assessment). */
export function OnenceSimCheck({ kind, id, label = 'Check 1NCE SIM' }: { kind: OnenceSubjectKind; id: string; label?: string }) {
  const [a, setA] = useState<OnenceAssessment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setA(await checkOnenceSim(kind, id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-onence-sim-check={kind}>
      <button type="button" onClick={run} disabled={busy} className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 disabled:opacity-50">
        {busy ? 'Checking…' : a ? 'Check again' : label}
      </button>
      {error && <p className="text-xs text-red-300">{error}</p>}
      {a && <OnenceAssessmentView a={a} />}
    </div>
  );
}

/** Device Details: the SIM summary, loaded ONCE when shown (no polling, no request loop; the server's
 *  60 s cache and request budget still apply). "Check again" is a manual re-read. Loading never
 *  changes a SIM: the SIM actions panel sends a request only after an explicit confirmation, and the
 *  summary is read again afterwards. */
export function OnenceDeviceSim({ id, radioName }: { id: string; radioName?: string }) {
  const [a, setA] = useState<OnenceAssessment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef<string | null>(null);
  useEffect(() => {
    if (current.current === id) return; // already loaded (or loading) for this radio
    current.current = id;
    setA(null);
    loadDeviceSim(id, current, { setA, setBusy, setError });
  }, [id]);
  return (
    <div className="space-y-2" data-onence-device-sim>
      {!a && busy && <p className="text-xs text-gray-400">Loading 1NCE SIM…</p>}
      {error && <p className="text-xs text-red-300">{error}</p>}
      {a && <OnenceSimSummary a={a} />}
      {a?.actions && (
        <OnenceSimActions radioId={id} panel={a.actions} simLabel={a.sim?.label} radioName={radioName}
          onDone={() => loadDeviceSim(id, current, { setA, setBusy, setError })} />
      )}
      {a && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-400">Evidence and cross-checks</summary>
          <div className="mt-2"><OnenceAssessmentView a={a} /></div>
        </details>
      )}
      {(a || error) && (
        <button type="button" onClick={() => loadDeviceSim(id, current, { setA, setBusy, setError })} disabled={busy}
          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 disabled:opacity-50">
          {busy ? 'Checking…' : 'Check again'}
        </button>
      )}
    </div>
  );
}

/** One SIM check for Device Details; an answer for a radio no longer shown is dropped. */
async function loadDeviceSim(id: string, current: { current: string | null }, set: {
  setA: (a: OnenceAssessment) => void; setBusy: (b: boolean) => void; setError: (e: string | null) => void;
}) {
  set.setBusy(true);
  set.setError(null);
  try {
    const r = await checkOnenceSim('radio', id);
    if (current.current === id) set.setA(r);
  } catch (err) {
    if (current.current === id) set.setError(err instanceof Error ? err.message : String(err));
  } finally {
    set.setBusy(false);
  }
}

/** Deployment: what 1NCE VPN / remote installation does and how it is gated. */
export function VpnReadinessNote() {
  return (
    <div className="text-xs text-gray-400 space-y-1" data-onence-vpn-note>
      <p>
        <span className="font-semibold text-gray-300">Remote installation over the 1NCE VPN is controlled by a server switch (off by default).</span> It
        installs only the model's VALIDATED current RadioOS release, only over the VPN. The SIM check below is read-only evidence (SIM, its IP
        address, data session, IMEI cross-check).
      </p>
      <p>1NCE Platform 2.0 VPN is one certificate-based connection per customer; its state is not visible to RadioOS.</p>
    </div>
  );
}
